import type { PollRead } from '@models/api/poll';

/** A row of a poll's ⋯ menu. */
export type PollMenuRow =
  'pin' | 'unpin' | 'bookmark' | 'edit' | 'end' | 'reopen' | 'report' | 'delete';

export interface PollMenuViewer {
  /** `is_my_question`. */
  isAuthor: boolean;
  /** An admin of the Space whose feed this is (`communityContext.isAdmin`; not a moderator). */
  isSpaceAdmin: boolean;
  /** `has_extra_options`, the backend's platform-admin answer. */
  isPlatformAdmin: boolean;
  /** Pinning acts on a Space's feed: none without one (the Home feed). */
  inSpaceFeed: boolean;
  isPinned: boolean;
}

/**
 * The ⋯ menu of a poll, in order, by role and state (TECH-197 § Editing, pin, delete; E1, E2, E13,
 * E19a, E19b):
 * - author: Pin poll (only if also a Space admin), Bookmark, Edit poll (before the first answer:
 *   `can_edit`, E1; absent in E2; a standard poll only, the live composer is TECH-867's), End poll
 *   now (open) or Reopen poll
 *   (ended, `can_reopen`: not after a platform admin ended it), Delete; never Report;
 * - platform admin: Pin poll, End poll now (`can_end`), Report, Delete (no Bookmark, as written);
 * - Space admin or moderator, and member: Pin poll (admins: the pin route is
 *   `SpaceAdminOrPlatformAdmin`), Bookmark, Report.
 * Report sits above Delete. Never "Crosspost"; "Remove from {Space}" has no route (CONTRACT F8).
 * End and Reopen follow the server's `can_end` / `can_reopen`.
 */
export const pollMenuRows = (
  poll: Pick<PollRead, 'can_end' | 'can_reopen'> & Partial<Pick<PollRead, 'can_edit' | 'mode'>>,
  viewer: PollMenuViewer,
): PollMenuRow[] => {
  const rows: PollMenuRow[] = [];
  if (viewer.inSpaceFeed && (viewer.isSpaceAdmin || viewer.isPlatformAdmin)) {
    rows.push(viewer.isPinned ? 'unpin' : 'pin');
  }
  if (viewer.isAuthor) {
    rows.push('bookmark');
    if (poll.can_edit === true && poll.mode !== 'live') rows.push('edit');
    if (poll.can_end) rows.push('end');
    else if (poll.can_reopen) rows.push('reopen');
    rows.push('delete');
    return rows;
  }
  if (viewer.isPlatformAdmin) {
    if (poll.can_end) rows.push('end');
    rows.push('report', 'delete');
    return rows;
  }
  rows.push('bookmark', 'report');
  return rows;
};
